<template>
  <div class="settings-chatbot">
    <div class="settings-chatbot__title">{{ $tr('settings.chatbot.settings') }}</div>

    <vpreloader v-if="isLoading" width="2" size="64" />

    <div v-else class="settings-chatbot__body">
      <vbutton :text="$tr('settings.chatbot.add-connection')" @click="openNewModal()" />

      <div v-if="currentConnectors.length > 0" class="settings-chatbot__list">
        <div class="settings-chatbot__list-item settings-chatbot__list-item--strong">
          <div class="settings-chatbot__list-item-type">{{ $tr('settings.chatbot.messenger') }}</div>

          <div class="settings-chatbot__list-item-name">{{ $tr('settings.chatbot.bot-name') }}</div>

          <div class="settings-chatbot__list-item-action"></div>
        </div>

        <div v-for="item in currentConnectors" :key="item.id" class="settings-chatbot__list-item">
          <div class="settings-chatbot__list-item-type">
            {{ getMessengerItem(item.messenger_type).name }}
          </div>

          <div class="settings-chatbot__list-item-name">{{ item.username }}</div>

          <div class="settings-chatbot__list-item-action">
            <vbutton
              :disabled="isLoadingForms"
              :text="$tr('common.edit')"
              size="small"
              @click="openEditModal(item.id)"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- modal -->
    <modal v-if="isShowAddConnection" redesign :has-footer="false" @close="isShowAddConnection = false">
      <div slot="header">
        <div class="modal-video-title">{{ $tr('settings.chatbot.bot-connection') }}</div>
      </div>

      <div slot="body">
        <div class="settings-chatbot__field">
          <div class="settings-chatbot__field-label">{{ $tr('settings.chatbot.access-token') }}</div>

          <vinput
            v-model="form.apiKey"
            v-validate="'required'"
            name="api"
            :class="{ error: errors.has('api') }"
            border
          />
        </div>

        <div v-if="!editMode" class="settings-chatbot__field">
          <div class="settings-chatbot__field-label">{{ $tr('settings.chatbot.messenger') }}</div>

          <multiselect
            v-model="form.messenger"
            class="settings-chatbot__field-select"
            :options="messengerOptions"
            :searchable="false"
            :allow-empty="false"
            :show-labels="false"
            label="name"
            track-by="code"
            open-direction="bottom"
          />
        </div>

        <div class="settings-chatbot__field-button">
          <vbutton
            :text="editMode ? $tr('actions.save') : $tr('actions.add')"
            :disabled="(!editMode && form.apiKey.length === 0) || isLoadingForms"
            @click="updateConnection()"
          />
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import Axios from 'axios';
import Modal from '@/components/Modal';

export default {
  name: 'VcvSettingsChatbot',

  components: { Modal },

  data() {
    return {
      isShowAddConnection: false,
      isLoading: false,
      isLoadingForms: false,
      messengerOptions: [{ code: 1, name: 'Telegram' }],
      currentConnectors: [],
      currentConnector: null,
      editMode: false,
      form: {
        apiKey: '',
        messenger: { code: 1, name: 'Telegram' }
      }
    };
  },

  computed: {
    ...mapState('vacancy_new', { vacancy: 'item' })
  },

  async created() {
    await this.fetchChatbotConnectors();
  },

  methods: {
    async fetchChatbotConnectors() {
      this.isLoading = true;

      await Axios.get('/v3/chatbot/connectors')
        .then(response => {
          this.currentConnectors = response.data._embedded.connectors;
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    async updateConnection() {
      this.isLoadingForms = true;

      if (!this.editMode) {
        const params = {
          api_key: this.form.apiKey,
          messenger_type: this.form.messenger.code
        };

        await Axios.post('/v3/chatbot/connectors', params)
          .then(async response => {
            if (response.status === 200 || response.status === 201) {
              this.isShowAddConnection = false;
              this.resetForm();

              await this.fetchChatbotConnectors();
            }
          })
          .catch(error => {
            if (error.response.status === 400) {
              this.$noty.error(this.$tr('invites.warning.title'));
            } else {
              this.$noty.success(this.$tr('vacancy.basic.form.modules.informers.save_error'));
            }
          })
          .finally(() => {
            this.isLoadingForms = false;
          });
      } else {
        const params = { api_key: this.form.apiKey, messenger_type: this.form.messenger.code };

        await Axios.patch(`/v3/chatbot/connectors/${this.currentConnector.id}`, params)
          .then(async response => {
            if (response.status === 200 || response.status === 201) {
              this.isShowAddConnection = false;
              this.resetForm();

              await this.fetchChatbotConnectors();
            }
          })
          .catch(() => {
            this.$noty.error(this.$tr('vacancy.basic.form.modules.informers.save_error'));
          })
          .finally(() => {
            this.isLoadingForms = false;
          });
      }
    },

    getMessengerItem(messengerType) {
      const messenger = this.messengerOptions.find(x => x.code === Number(messengerType));

      if (messenger !== undefined) {
        return messenger;
      }
    },

    openEditModal(connectorId) {
      this.isShowAddConnection = true;
      this.editMode = true;

      const connector = this.currentConnectors.find(x => x.id === connectorId);
      this.currentConnector = connector;
      this.form.apiKey = '';
      this.form.messengerType = this.getMessengerItem(connector.messenger_type);
    },

    openNewModal() {
      this.resetForm();
      this.isShowAddConnection = true;
      this.editMode = false;
    },

    resetForm() {
      this.form = {
        apiKey: '',
        messenger: this.messengerOptions[0]
      };
    }
  }
};
</script>

<style lang="scss" scoped>
@import '@/styles/fonts.scss';

.settings-chatbot {
  margin-bottom: 50px;

  &__title {
    @include mixin-text-header-bigger;
  }

  &__body {
    margin: 30px 0;
  }

  &__field {
    margin-bottom: 20px;
  }

  &__field-label {
    margin-bottom: 5px;
    color: #666;
    font-size: 14px;
  }

  &__field-select {
    width: 100%;
    max-width: 100%;
  }

  &__field-button {
    margin-top: 30px;
  }

  &__list {
    margin-top: 45px;
    max-width: 700px;
  }

  &__list-item {
    padding: 15px 0;
    border-bottom: 1px solid #eee;
    display: flex;
    align-items: center;
    justify-content: space-between;

    > div {
      padding: 0 10px;
    }

    &--strong {
      font-weight: 500;
    }
  }

  &__list-item-type {
    flex: 0 0 150px;
  }

  &__list-item-name {
    flex: 1 1 auto;
  }

  &__list-item-action {
    flex: 0 0 150px;
  }
}
</style>
